import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { MessageBubbleProps } from '../types/chat';
import { VCH } from '../theme/vendorChatChaavi';

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwnMessage,
  showSenderName = false,
  variant = 'default',
}) => {
  const isChaavi = variant === 'chaavi';
  const isVendorChaavi = variant === 'vendor-chaavi';
  const initial = (message.senderName || (message.senderType === 'CLIENT' ? 'C' : 'V')).charAt(0).toUpperCase();

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSenderIcon = () => {
    return message.senderType === 'CLIENT' ? <PersonIcon /> : <StoreIcon />;
  };

  const getSenderColor = () => {
    return message.senderType === 'CLIENT' ? 'primary' : 'secondary';
  };

  if (isVendorChaavi) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
          flexDirection: isOwnMessage ? 'row-reverse' : 'row',
          mb: 1.25,
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: 11,
            fontWeight: 600,
            bgcolor: isOwnMessage ? '#fde8c8' : '#f0ebe0',
            color: isOwnMessage ? VCH.accent : VCH.barMuted,
            fontFamily: 'sans-serif',
          }}
        >
          {isOwnMessage ? 'Y'.charAt(0) : initial}
        </Avatar>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isOwnMessage ? 'flex-end' : 'flex-start', maxWidth: '68%' }}>
          {showSenderName && !isOwnMessage && (
            <Typography variant="caption" sx={{ color: VCH.muted, mb: 0.25, display: 'block' }}>
              {message.senderName}
            </Typography>
          )}
          <Box
            sx={{
              px: '13px',
              py: '9px',
              borderRadius: '14px',
              fontSize: 13,
              fontFamily: 'sans-serif',
              lineHeight: 1.5,
              bgcolor: isOwnMessage ? VCH.bubbleOut : VCH.bubbleInBg,
              color: isOwnMessage ? '#fff' : VCH.ink,
              border: isOwnMessage ? 'none' : `0.5px solid ${VCH.border}`,
              borderBottomLeftRadius: isOwnMessage ? 14 : 4,
              borderBottomRightRadius: isOwnMessage ? 4 : 14,
              wordBreak: 'break-word',
            }}
          >
            {message.message}
            {isOwnMessage && (
              <Typography component="span" sx={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', ml: 0.5 }}>
                {message.isRead ? '✓✓' : '✓'}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: 10,
              mt: 0.5,
              fontFamily: 'sans-serif',
              color: isOwnMessage ? 'rgba(255,255,255,0.65)' : VCH.dateMuted,
              textAlign: isOwnMessage ? 'right' : 'left',
            }}
          >
            {formatTime(message.createdAt)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
        mb: 2,
        alignItems: 'flex-end',
      }}
    >
      {!isOwnMessage && (
        <Avatar
          sx={{
            bgcolor: isChaavi ? '#c9a227' : `${getSenderColor()}.main`,
            color: '#fff',
            width: 32,
            height: 32,
            mr: 1,
          }}
        >
          {getSenderIcon()}
        </Avatar>
      )}
      
      <Box sx={{ maxWidth: '70%' }}>
        {showSenderName && !isOwnMessage && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1, mb: 0.5, display: 'block' }}
          >
            {message.senderName}
          </Typography>
        )}
        
        <Paper
          elevation={isChaavi ? 0 : 2}
          sx={{
            p: 2,
            ...(isOwnMessage && isChaavi
              ? {
                  background: 'linear-gradient(135deg, #d4a84a 0%, #b8860b 100%)',
                  color: '#fff',
                }
              : {
                  bgcolor: isOwnMessage ? 'primary.main' : isChaavi ? '#ffffff' : 'grey.100',
                  color: isOwnMessage ? 'white' : isChaavi ? '#2e261c' : 'text.primary',
                }),
            border: isChaavi && !isOwnMessage ? '1px solid rgba(139, 105, 20, 0.18)' : undefined,
            borderRadius: isOwnMessage ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            position: 'relative',
            boxShadow: isChaavi ? '0 2px 8px rgba(46, 38, 28, 0.06)' : undefined,
          }}
        >
          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
            {message.message}
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isOwnMessage ? 'rgba(255,255,255,0.85)' : isChaavi ? 'rgba(46, 38, 28, 0.5)' : 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              {formatTime(message.createdAt)}
            </Typography>
            
            {isOwnMessage && (
              <Box sx={{ ml: 1 }}>
                {message.isRead ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.7rem',
                    }}
                  >
                    ✓✓
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.7rem',
                    }}
                  >
                    ✓
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Paper>
        
        {message.messageType !== 'TEXT' && (
          <Chip
            label={message.messageType}
            size="small"
            color={getSenderColor() as any}
            sx={{ mt: 0.5, ml: 1 }}
          />
        )}
      </Box>
      
      {isOwnMessage && (
        <Avatar
          sx={{
            bgcolor: isChaavi ? '#7a5c1a' : 'primary.main',
            width: 32,
            height: 32,
            ml: 1,
          }}
        >
          <PersonIcon />
        </Avatar>
      )}
    </Box>
  );
};

export default MessageBubble;
